import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { 
  Store, Check, ArrowRight, Users, Clock, CreditCard,
  TrendingUp, Star, Zap
} from 'lucide-react';

const VenuePage = () => {
  return (
    <div data-testid="venue-page" className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden" style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-72 h-72 bg-lime-400 rounded-full blur-3xl" />
        </div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-lime-400 rounded-full px-4 py-2 mb-6">
              <Store className="w-4 h-4 text-slate-900" />
              <span className="text-sm font-semibold text-slate-900">Karikim për Venue</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white font-['Inter_Tight'] tracking-tight mb-6">
              Tërheqni Klientë me Karikimin EV
            </h1>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              Shndërroni biznesin tuaj në një destinacion miqësor me automjetet elektrike. 
              Rrisni trafikun e klientëve dhe kohën që ata kalojnë tek ju.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Button 
                asChild
                className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-semibold"
              >
                <Link to="/contact">
                  Kërkoni Ofertë
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
      
      {/* For Who Section */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Inter_Tight'] mb-4">
              Ideale për Çdo Lloj Biznesi
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { 
                title: 'Qendra Tregtare', 
                desc: 'Ofroni karikues në parking dhe mbani klientët më gjatë duke blerë',
                image: 'https://images.unsplash.com/photo-1758670496782-cb11b7b56675?crop=entropy&cs=srgb&fm=jpg&w=400&q=80'
              },
              { 
                title: 'Restorante & Bare', 
                desc: 'Klientët karikojnë makinën ndërsa shijojnë vaktin ose kafenë',
                image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?crop=entropy&cs=srgb&fm=jpg&w=400&q=80'
              },
              { 
                title: 'Supermarkete', 
                desc: 'Karikim i shpejtë gjatë kohës së blerjeve të përditshme',
                image: 'https://images.unsplash.com/photo-1604719312566-8912e9227c6a?crop=entropy&cs=srgb&fm=jpg&w=400&q=80'
              }
            ].map((item, index) => (
              <div key={index} className="group bg-slate-50 rounded-3xl overflow-hidden">
                <div className="h-48 overflow-hidden">
                  <img 
                    src={item.image} 
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-slate-900 font-['Inter_Tight'] mb-2">{item.title}</h3>
                  <p className="text-slate-500">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Benefits */}
      <section className="section-padding bg-slate-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 font-['Inter_Tight'] mb-6">
                Përfitimet për Biznesin Tuaj
              </h2>
              
              <div className="space-y-6">
                {[
                  { icon: Users, title: 'Rritje e Trafikut', desc: 'Tërheqni klientë të rinj që kërkojnë vende karikimi' },
                  { icon: Clock, title: 'Kohë më e Gjatë', desc: 'Klientët qëndrojnë më gjatë ndërsa karikojnë' },
                  { icon: TrendingUp, title: 'Imazh Modern', desc: 'Pozicionohuni si biznes i qëndrueshëm dhe inovativ' },
                  { icon: CreditCard, title: 'Të Ardhura Shtesë', desc: 'Fitoni nga çdo sesion karikimi ose ofroni falas si bonus' }
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-lime-100 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-6 h-6 text-lime-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-1">{item.title}</h3>
                      <p className="text-slate-500 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="bg-white rounded-3xl p-8 shadow-xl">
              <div className="flex items-center gap-3 mb-6 pb-6 border-b border-slate-100">
                <Star className="w-8 h-8 text-yellow-500" />
                <div>
                  <p className="text-2xl font-bold text-slate-900">4.9/5</p>
                  <p className="text-slate-500 text-sm">Vlerësimi mesatar</p>
                </div>
              </div>
              
              <blockquote className="text-slate-600 italic mb-4">
                "Që kur instaluam karikuesit AlbMove, kemi parë rritje të dukshme të klientëve që vijnë posaçërisht 
                sepse ofrojmë këtë shërbim. Investim i shkëlqyer!"
              </blockquote>
              <p className="text-slate-900 font-semibold">— Pronar Restoranti, Tiranë</p>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="rounded-3xl p-12 text-center" style={{ background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)' }}>
            <h2 className="text-3xl md:text-4xl font-bold text-white font-['Inter_Tight'] mb-4">
              Bëhuni Destinacion EV-Friendly
            </h2>
            <p className="text-blue-100 mb-8 max-w-xl mx-auto">
              Kontaktoni ekipin tonë për të diskutuar zgjidhjen më të mirë për biznesin tuaj.
            </p>
            <Button 
              asChild
              className="bg-lime-400 hover:bg-lime-300 text-slate-900 rounded-full px-8 py-6 text-lg font-semibold"
            >
              <Link to="/contact">
                Filloni Tani
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default VenuePage;
